import React, { useState, useEffect, useRef } from "react";
import {
  View,
  TouchableOpacity,
  I18nManager,
  Animated,
  Easing,
  StyleProp,
  ViewStyle,
  ViewProps,
} from "react-native";
import Collapsible from "react-native-collapsible";
import type { CollapsibleProps } from "react-native-collapsible";
import ArrowDownIcon, { ArrowDownIconStyling } from "./ArrowDownIcon";
import { MakePropsOptional } from "./types";
import { VDBText } from "@components/atoms";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { getBoxShadow } from "@utils/product/productValidators";
import { SpaceType } from "@components/atoms/Spacer/types";
import { DefaultTheme } from "styled-components/native";

interface CollapsibleViewProps {
  children: React.ReactNode;
  title?: string | React.ReactNode;
  initExpanded?: boolean;
  expanded?: boolean | null;
  unmountOnCollapse?: boolean;
  isRTL?: boolean | "auto";
  duration?: number;
  collapsibleProps?: MakePropsOptional<CollapsibleProps>;
  collapsibleContainerStyle?: ViewStyle;
  arrowStyling?: ArrowDownIconStyling;
  noArrow?: boolean;
  style?: StyleProp<ViewStyle>;
  activeOpacityFeedback?: number; // 0-1
  TouchableComponent?: React.ComponentType<any>;
  titleProps?: ViewProps;
  titleStyle?: ViewStyle;
  touchableWrapperProps?: ViewProps;
  applyShadow?: boolean;
  applyTopPadding?: boolean;
  applyBottomPadding?: boolean;
  alwaysExpanded?: boolean;
}

const CollapsibleView = ({
  children,
  title = "",
  initExpanded = false,
  expanded = null,
  unmountOnCollapse = false,
  isRTL = "auto",
  duration = 300,
  collapsibleProps = {},
  collapsibleContainerStyle = {},
  arrowStyling,
  noArrow = false,
  activeOpacityFeedback = 0.3,
  TouchableComponent = TouchableOpacity,
  titleProps = {},
  titleStyle = {},
  touchableWrapperProps = {},
  applyShadow = false,
  applyTopPadding = true,
  applyBottomPadding = true,
  alwaysExpanded = false,
}: CollapsibleViewProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();

  let controlled = expanded !== null;
  const [show, setShow] = useState<boolean | null>(initExpanded);
  const [mounted, setMounted] = useState(initExpanded);

  const rotateAnim = useRef(new Animated.Value(0)).current;

  if (controlled) {
    if (!mounted && expanded) setMounted(true);
  }

  const handleArrowRotate = (open: boolean | null = null) => {
    const _open = open === null ? show : open;
    if (!_open)
      Animated.timing(rotateAnim, {
        toValue: -180,
        duration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    else {
      Animated.timing(rotateAnim, {
        toValue: rotateAngle,
        duration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }
  };

  const handleAnimationEnd = () => {
    if (unmountOnCollapse && !show) setMounted(false);
  };

  const handleToggleShow = () => {
    if (!controlled)
      if (!mounted) {
        if (!show) setMounted(true);
      } else {
        setShow(!show);
      }
  };

  // place the arrow on the left or the right based on the device direction and isRTL property
  let rowDir: "row-reverse" | "row" = "row" as const;
  if (isRTL === "auto") isRTL = I18nManager.isRTL;
  else if (isRTL !== I18nManager.isRTL) rowDir = "row-reverse";

  const rotateAngle = isRTL ? 90 : 0;
  const rotateAnimDeg = rotateAnim.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  const TitleElement =
    typeof title === "string" ? (
      <VDBText
        color={theme["core-05"]}
        type={typography["H3-M"]}
        selectable={true}
      >
        {title}
      </VDBText>
    ) : (
      title
    );

  useEffect(() => {
    if (mounted) {
      setShow((current) => (current === true ? current : true));
    }
  }, [mounted]);

  useEffect(() => {
    rotateAnim.setValue(show ? 180 : rotateAngle);
  }, []);

  useEffect(() => {
    if (mounted) handleArrowRotate(!show);
  }, [mounted, show]);

  useEffect(() => {
    if (controlled && show !== expanded) {
      setShow(expanded);
    }
  }, [controlled, show, expanded]);

  const TitleRow = (
    <View
      style={{
        flexDirection: rowDir,
        alignItems: "center",
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
        ...titleStyle,
      }}
      {...titleProps}
    >
      <CardMain>{TitleElement}</CardMain>
      {noArrow || alwaysExpanded ? null : (
        <Animated.View style={{ transform: [{ rotate: rotateAnimDeg }] }}>
          <ArrowDownIcon {...arrowStyling} />
        </Animated.View>
      )}
    </View>
  );

  if (alwaysExpanded) {
    return (
      <Container
        applyShadow={applyShadow}
        applyTopPadding={applyTopPadding}
        applyBottomPadding={applyBottomPadding}
      >
        {TitleRow}
        <View style={{ width: "100%", ...collapsibleContainerStyle }}>
          {children}
        </View>
      </Container>
    );
  }

  return (
    <Container
      applyShadow={applyShadow}
      applyTopPadding={applyTopPadding}
      applyBottomPadding={applyBottomPadding}
    >
      <View>
        <TouchableComponent
          onPress={handleToggleShow}
          activeOpacity={activeOpacityFeedback}
          {...touchableWrapperProps}
        >
          {TitleRow}
        </TouchableComponent>
      </View>
      {mounted ? (
        <View style={{ width: "100%", ...collapsibleContainerStyle }}>
          <Collapsible
            onAnimationEnd={handleAnimationEnd}
            collapsed={!show}
            {...{ duration, ...collapsibleProps }}
          >
            {children}
          </Collapsible>
        </View>
      ) : null}
    </Container>
  );
};

const CardMain = styled.View`
  flex: 1;
`;

const Container = styled.View<{
  applyShadow?: boolean;
  applyTopPadding?: boolean;
  applyBottomPadding?: boolean;
}>`
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  align-content: flex-start;
  border-radius: ${SpaceType.Basic}px;
  /* margin-vertical: 5px; */
  ${({ applyTopPadding }: { applyTopPadding: boolean }) =>
    applyTopPadding &&
    css`
      padding-top: 5px;
    `}
  ${({ applyBottomPadding }: { applyBottomPadding: boolean }) =>
    applyBottomPadding &&
    css`
      padding-bottom: 5px;
    `}
  ${({ theme, applyShadow }: { theme: DefaultTheme; applyShadow: boolean }) =>
    applyShadow &&
    css`
      box-shadow: ${getBoxShadow(theme.Shadows.Gentle)};
    `}
`;

export default CollapsibleView;
